import { useOktaAuth } from '@okta/okta-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLogoutHandler } from '../../../common/hooks/useLogoutHandler';
import { broadcastUserActivity, onCrossTabActivity, triggerSessionExpiredLogout } from '../../../common/sessionLogout';
import { AlertDialog } from '../AlertDialog/AlertDialog';

const countDownSecondsConst = 115;
const warningTimeConst = 900 - countDownSecondsConst;
const events = ['load', 'mousemove', 'mousedown', 'click', 'scroll', 'keypress'];
// Throttle how often local activity is broadcast to other tabs. The idle timer
// works in whole seconds, so a couple of seconds is precise enough and avoids
// flooding the channel on every mousemove.
const activityBroadcastThrottleMs = 2000;

function Timeout() {
    const { t } = useTranslation();
    const [warningTime, setWarningTime] = useState(warningTimeConst);
    const [popupOpen, setPopupOpen] = useState(false);
    const [countdownTime, setCountdownTime] = useState(countDownSecondsConst);
    const { authState, oktaAuth } = useOktaAuth();
    const isAuthenticated = authState?.isAuthenticated;
    const { handleLogout } = useLogoutHandler();
    const lastActivityBroadcastRef = useRef(0);

    useEffect(() => {
        const resetWarningTime = () => {
            setWarningTime(warningTimeConst);
            // Let other open tabs know the user is active here so their per-tab
            // idle timers stay alive and an idle tab doesn't log this one out.
            const now = Date.now();
            if (now - lastActivityBroadcastRef.current > activityBroadcastThrottleMs) {
                lastActivityBroadcastRef.current = now;
                broadcastUserActivity();
            }
        };
        events.forEach((event) => {
            window.addEventListener(event, resetWarningTime);
        });
        return () => {
            events.forEach((event) => {
                window.removeEventListener(event, resetWarningTime);
            });
        };
    }, []);

    // Keep this tab's idle timer alive when the user is active in another tab.
    // Resetting the timer and dismissing the warning popup here is what prevents
    // an idle tab from force-logging-out a tab the user is actively working in.
    useEffect(() => {
        const unsubscribe = onCrossTabActivity(() => {
            setWarningTime(warningTimeConst);
            setPopupOpen(false);
            setCountdownTime(countDownSecondsConst);
        });
        return unsubscribe;
    }, []);

    useEffect(() => {
        if (!isAuthenticated || popupOpen) return;
        const counter = setInterval(() => setWarningTime(warningTime - 1), 1000);

        if (!popupOpen && warningTime === 0) {
            setPopupOpen(true);
            setCountdownTime(countDownSecondsConst);
        }
        return () => {
            clearInterval(counter);
        };
    }, [warningTime, popupOpen, isAuthenticated]);

    useEffect(() => {
        if (!isAuthenticated || !popupOpen) return;
        const countdownCounter = window.setInterval(() => setCountdownTime(countdownTime - 1), 1000);

        if (countdownTime === 0) {
            console.log('[session-auth] timeout countdown reached zero - triggering session-expired logout');
            triggerSessionExpiredLogout();
        }
        return () => {
            clearInterval(countdownCounter);
        };
    }, [popupOpen, countdownTime, isAuthenticated]);

    // When the tab regains focus, log out only if the access token has expired
    // in the background. A silent renew is attempted first so that a valid
    // refresh token keeps the user signed in instead of a false-positive logout.
    useEffect(() => {
        if (!isAuthenticated) return;
        const checkTokenAndLogout = async () => {
            try {
                const tokens = await oktaAuth?.tokenManager?.getTokens();
                const accessToken = tokens?.accessToken;
                if (accessToken && !oktaAuth.tokenManager.hasExpired(accessToken)) {
                    console.log('[session-auth] Tab visible: access token still valid - staying signed in');
                    return; // token still valid, nothing to do
                }
                console.log('[session-auth] Tab visible: access token missing/expired - attempting silent renew');
                try {
                    await oktaAuth.tokenManager.renew('accessToken');
                    console.log('[session-auth] Tab visible: token renew succeeded - session kept');
                    return; // renewed successfully, keep the session
                } catch {
                    console.log('[session-auth] Tab visible: token renew failed - triggering session-expired logout');
                    triggerSessionExpiredLogout();
                }
            } catch (err) {
                console.error('Visibility token check failed', err);
            }
        };
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') checkTokenAndLogout();
        };
        // Safari/Firefox restore from the bfcache without firing visibilitychange.
        const handlePageShow = (event: PageTransitionEvent) => {
            if (event.persisted) {
                console.log('[session-auth] Page restored from bfcache - re-checking token');
                checkTokenAndLogout();
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('pageshow', handlePageShow);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('pageshow', handlePageShow);
        };
    }, [isAuthenticated, oktaAuth]);

    const handleDismiss = () => {
        setPopupOpen(false);
        setWarningTime(warningTimeConst);
        setCountdownTime(countDownSecondsConst);
    };

    if (!isAuthenticated) return null;

    return (
        <AlertDialog
            type="confirmation"
            open={popupOpen}
            handleClose={handleDismiss}
            content={`${t('TIMEOUT_MODAL_CONTENT', { remainingSeconds: countdownTime })}`}
            title={t('TIMEOUT_MODAL_TITLE')}
            aria-label="stay-signed-in-session-expired-modal"
            data-testid="session-expired-modal"
            cancelButtonLabel={t('TIMEOUT_MODAL_SIGNOUT_BUTTON')}
            confirmButtonLabel={t('TIMEOUT_MODAL_STAY_SIGNIN_BUTTON')}
            onCancellation={handleLogout}
            onConfirmation={handleDismiss}
        />
    );
}

export default Timeout;
